// import { Link } from "react-router-dom";
import { useState, useRef, useEffect, useCallback } from "react";
import DotLoader from "react-spinners/DotLoader";
import axios from "axios";
import { useDispatch } from "react-redux";
import Cookies from "js-cookie";
import { useNavigate } from "react-router-dom";

const API_URL = process.env.REACT_APP_API_URL || "";
const ATOZAS_SSO_ENABLED =
  String(process.env.REACT_APP_ATOZAS_SSO_ENABLED).toLowerCase() === "true";
const ATOZAS_AUTO_REDIRECT =
  String(process.env.REACT_APP_ATOZAS_AUTO_REDIRECT).toLowerCase() === "true";

function WelcomeIcon() {
  return (
    <svg viewBox="0 0 64 64" width="64" height="64" aria-hidden="true">
      <circle cx="32" cy="32" r="32" fill="#F06292" />
      <ellipse cx="32" cy="34" rx="16" ry="14" fill="#fff" />
      <circle cx="26" cy="32" r="2.2" fill="#F06292" />
      <circle cx="38" cy="32" r="2.2" fill="#F06292" />
      <path
        d="M26 38c2.2 2.4 9.8 2.4 12 0"
        stroke="#F06292"
        strokeWidth="2.2"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}

function MailIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      <rect
        x="3"
        y="5"
        width="18"
        height="14"
        rx="3"
        fill="none"
        stroke="#F06292"
        strokeWidth="1.8"
      />
      <path
        d="M4.5 7.5L12 13l7.5-5.5"
        fill="none"
        stroke="#F06292"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function PlaneIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <path
        d="M3.5 11.5L20 4l-5.5 16-3.2-5.8L3.5 11.5z"
        fill="currentColor"
      />
    </svg>
  );
}

function UserPlusIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <circle cx="10" cy="8" r="3.2" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <path
        d="M4.5 18.5c.8-3 3-4.5 5.5-4.5s4.7 1.5 5.5 4.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
      <path
        d="M18 8v6M15 11h6"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

function ShieldIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
      <path
        d="M12 3l7 3v5.5c0 4.4-2.9 7.4-7 9.5-4.1-2.1-7-5.1-7-9.5V6l7-3z"
        fill="#7B5CFF"
      />
      <path
        d="M9.2 12.1l1.8 1.8 3.8-3.8"
        fill="none"
        stroke="#fff"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function LoginForm({ setVisible }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Step state: 0 = enter email, 1 = enter OTP, 2 = deactivated account
  const [step, setStep] = useState(0);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState(["", "", "", ""]);
  const [, setOtpSent] = useState(false);
  const [otpError, setOtpError] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  const [error, setError] = useState("");
  const [googleLoading, setGoogleLoading] = useState(false);
  const [googleScriptReady, setGoogleScriptReady] = useState(false);
  const [reactivateToken, setReactivateToken] = useState("");
  const [reactivateLoading, setReactivateLoading] = useState(false);
  const otpRefs = useRef([]);

  const completeLogin = useCallback(
    (data) => {
      dispatch({ type: "LOGIN", payload: data });
      Cookies.set("user", JSON.stringify(data));
      navigate("/");
      window.location.reload();
    },
    [dispatch, navigate]
  );

  // Kick off ATOZAS SSO: a full-page (top-level) navigation to the backend,
  // which builds state + PKCE and redirects to the ATOZAS Identity Provider.
  const startAtozasLogin = useCallback(() => {
    // Deliberate login intent — clear the explicit-logout guard so the SSO
    // session can be restored again after the redirect completes.
    try {
      localStorage.removeItem("atozas_logged_out");
    } catch (e) {
      /* ignore storage errors */
    }
    const returnTo = "/";
    window.location.href = `${API_URL}/auth/atozas?returnTo=${encodeURIComponent(
      returnTo
    )}`;
  }, []);

  // Optional: auto-redirect to ATOZAS when configured (SSO-only login).
  useEffect(() => {
    if (ATOZAS_SSO_ENABLED && ATOZAS_AUTO_REDIRECT) {
      startAtozasLogin();
    }
  }, [startAtozasLogin]);

  const handleDeactivatedLogin = useCallback((data) => {
    setReactivateToken(data.reactivateToken || "");
    setEmail(data.email || "");
    setOtpError("");
    setError("");
    setStep(2);
  }, []);

  // Poll for Google script (loads async in index.html) so we re-render when ready
  useEffect(() => {
    const check = () => {
      if (window.google?.accounts?.id) {
        setGoogleScriptReady(true);
        return true;
      }
      return false;
    };
    if (check()) return;
    const id = setInterval(() => check() && clearInterval(id), 200);
    const timeout = setTimeout(() => clearInterval(id), 15000);
    return () => {
      clearInterval(id);
      clearTimeout(timeout);
    };
  }, []);

  // Send OTP to email for login
  const handleSendOtp = async () => {
    setOtpError("");
    setOtpLoading(true);
    try {
      await axios.post(`/api/auth/send-login-otp`, { email });
      setOtpSent(true);
      setStep(1);
    } catch (err) {
      setOtpError(
        err.response?.data?.message || "Failed to send OTP. Please try again."
      );
    }
    setOtpLoading(false);
  };

  // Verify OTP and login
  const handleVerifyOtp = useCallback(
    async (e) => {
      if (e) e.preventDefault();
      const currentOtpString = otp.join("");
      if (currentOtpString.length !== 4) return;

      setOtpError("");
      setOtpLoading(true);
      try {
        const { data } = await axios.post(`/api/auth/login-otp`, {
          email,
          otp: currentOtpString,
        });

        if (data.deactivated) {
          handleDeactivatedLogin(data);
          return;
        }

        completeLogin(data);
      } catch (err) {
        // Clear OTP on error
        setOtp(["", "", "", ""]);
        // Focus back to first input
        if (otpRefs.current[0]) otpRefs.current[0].focus();
        setOtpError(
          err.response?.data?.message || "Invalid OTP. Please try again."
        );
      } finally {
        setOtpLoading(false);
      }
    },
    [otp, email, completeLogin, handleDeactivatedLogin]
  );

  const handleOtpChange = (e, idx) => {
    const value = e.target.value.replace(/\D/g, "");
    if (!value) return;
    const newOtp = [...otp];
    newOtp[idx] = value[0];
    setOtp(newOtp);
    if (idx < 3 && value) {
      otpRefs.current[idx + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (e, idx) => {
    if (e.key === "Backspace") {
      if (otp[idx]) {
        const newOtp = [...otp];
        newOtp[idx] = "";
        setOtp(newOtp);
      } else if (idx > 0) {
        otpRefs.current[idx - 1]?.focus();
      }
    }
  };

  const otpString = otp.join("");

  // Auto-verify OTP when all 4 digits are entered
  useEffect(() => {
    if (otpString.length === 4 && step === 1 && !otpLoading) {
      handleVerifyOtp();
    }
  }, [otpString, step, otpLoading, handleVerifyOtp]);

  const handleGoogleCredential = useCallback(
    async (response) => {
      try {
        setGoogleLoading(true);
        setError("");
        const token = response.credential;
        const { data } = await axios.post(`/api/auth/google`, { token });

        if (data.deactivated) {
          handleDeactivatedLogin(data);
          return;
        }

        completeLogin(data);
      } catch (err) {
        setError(err.response?.data?.message || "Google login failed.");
      } finally {
        setGoogleLoading(false);
      }
    },
    [completeLogin, handleDeactivatedLogin]
  );

  const handleReactivateAccount = async () => {
    if (!reactivateToken) {
      setError("Reactivation session expired. Please log in again.");
      setStep(0);
      return;
    }

    setReactivateLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API_URL}/reactivateAccount`, {
        reactivateToken,
      });
      completeLogin(data);
    } catch (err) {
      const message =
        err.response?.data?.message ||
        "Failed to reactivate account. Please try logging in again.";
      setError(message);
      setReactivateToken("");
      // After permanent deletion (410) or other failures, return to login
      setStep(0);
    } finally {
      setReactivateLoading(false);
    }
  };

  useEffect(() => {
    if (!googleScriptReady || !window.google?.accounts?.id || step === 2) return;
    try {
      window.google.accounts.id.initialize({
        client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
        callback: handleGoogleCredential,
      });
      const div = document.getElementById("googleSignInDiv");
      if (div && div.childElementCount === 0) {
        const btnWidth = Math.min(
          320,
          Math.max(260, Math.floor(div.clientWidth || window.innerWidth - 48))
        );
        window.google.accounts.id.renderButton(div, {
          theme: "outline",
          size: "large",
          text: "continue_with",
          shape: "pill",
          width: btnWidth,
        });
      }

      const updateGoogleButtonLabel = () => {
        const root = document.getElementById("googleSignInDiv");
        if (!root) return;

        const googleLabel = "Continue with Google login";
        const walker = document.createTreeWalker(
          root,
          NodeFilter.SHOW_TEXT,
          null
        );

        let node;
        while ((node = walker.nextNode())) {
          if (node.textContent?.includes("Continue with Google")) {
            node.textContent = node.textContent.replace(
              /Continue with Google(?: login)?/g,
              googleLabel
            );
            return;
          }
        }
      };

      requestAnimationFrame(updateGoogleButtonLabel);
      setTimeout(updateGoogleButtonLabel, 150);
    } catch (e) {
      console.error("Google init error", e);
    }
  }, [handleGoogleCredential, googleScriptReady, step]);

  return (
    <div className="login_wrap">
      <div className="login_1">
        <div className="brand_block">
          <p className="brand_welcome">Welcome to</p>
          <div className="brand_row">
            <h1 className="brand_name">USMAAPP</h1>
            <span className="brand_chat_icon">
              <img
                src="/icons/app-icon-512.png"
                alt="UsmaApp"
                className="brand_app_icon"
              />
            </span>
          </div>
          <p className="brand_sub">Universal Social Media Advisory App</p>
        </div>

        <p className="login_headline login_headline_desktop">
          UsmaApp – a calm space to{" "}
          <span className="hl hl_purple">connect</span>,{" "}
          <span className="hl hl_pink">share</span> your moments in the most{" "}
          <span className="hl hl_purple">beautiful</span> and{" "}
          <span className="hl hl_blue">effortless</span> way.
        </p>
        <p className="login_headline login_headline_mobile">
          UsmaApp – a calm space to{" "}
          <span className="hl hl_pink">connect</span>, share your moments in
          the most beautiful and effortless way.
        </p>

        <div className="login_hero_art">
          <img
            src="/images/socialNetwork.png"
            alt="People connecting on UsmaApp"
          />
        </div>
      </div>

      <div className="login_2">
        <div className="login_2_wrap">
          <div className="auth_card_badge">
            <WelcomeIcon /> 
            <span className="spark spark_1" />
            <span className="spark spark_2" />
            <span className="spark spark_3" />
          </div>

          <div className="auth_card_header">
            <h2>
              {step === 2
                ? "Welcome back"
                : step === 1
                ? "Check your inbox"
                : "Welcome Back!"}
            </h2>
            <p>
              {step === 2
                ? "Restore your account to pick up where you left off."
                : step === 1
                ? `We sent a 4-digit code to ${email}`
                : "Login to continue to UsmaApp"}
            </p>
          </div>

          {step === 2 ? (
            <div className="deactivated_account_panel">
              <p className="deactivated_account_message">
                Your account is currently deactivated
              </p>
              <p className="deactivated_account_description">
                Reactivate within 15 days of deactivation to restore your
                profile and content. After 15 days, your account and all
                associated data are permanently deleted.
              </p>
              {error && <div className="error_text">{error}</div>}
              <button
                className="blue_btn reactivate_account_btn"
                type="button"
                onClick={handleReactivateAccount}
                disabled={reactivateLoading}
              >
                {reactivateLoading ? (
                  <div className="btn_loading">
                    <DotLoader color="#ffffff" size={16} />
                    <span>Reactivating...</span>
                  </div>
                ) : (
                  "Reactivate Account"
                )}
              </button>
              <button
                className="gray_btn auth_secondary"
                type="button"
                onClick={() => {
                  setStep(0);
                  setReactivateToken("");
                  setOtp(["", "", "", ""]);
                  setError("");
                }}
                disabled={reactivateLoading}
              >
                Back to login
              </button>
            </div>
          ) : (
            <>
              {step === 0 && (
                <form className="auth_form">
                  <label className="auth_input_wrap" htmlFor="login_email">
                    <span className="auth_input_icon">
                      <MailIcon />
                    </span>
                    <input
                      id="login_email"
                      type="email"
                      placeholder="Enter your email address"
                      name="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="register_plain_input auth_input"
                    />
                  </label>
                  <button
                    className="blue_btn auth_submit"
                    type="button"
                    onClick={handleSendOtp}
                    disabled={otpLoading || !email}
                  >
                    {otpLoading ? (
                      <div className="btn_loading">
                        <DotLoader color="#ffffff" size={16} />
                        <span>Sending OTP...</span>
                      </div>
                    ) : (
                      <>
                        <PlaneIcon />
                        <span>Send OTP login</span>
                      </>
                    )}
                  </button>
                  {otpError && <div className="error_text">{otpError}</div>}
                </form>
              )}
              {step === 1 && (
                <form className="auth_form" onSubmit={handleVerifyOtp}>
                  <div className="otp_row">
                    {otp.map((digit, idx) => (
                      <input
                        key={idx}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleOtpChange(e, idx)}
                        onKeyDown={(e) => handleOtpKeyDown(e, idx)}
                        ref={(el) => (otpRefs.current[idx] = el)}
                        className="register_plain_input otp_input"
                      />
                    ))}
                  </div>
                  {otpError && <div className="error_text">{otpError}</div>}
                  <button
                    className="gray_btn auth_secondary"
                    type="button"
                    onClick={handleSendOtp}
                    disabled={otpLoading}
                  >
                    {otpLoading ? (
                      <div className="btn_loading">
                        <DotLoader color="#7B5CFF" size={16} />
                        <span>Resending...</span>
                      </div>
                    ) : (
                      "Resend OTP"
                    )}
                  </button>
                  <button
                    type="button"
                    className="auth_text_link"
                    onClick={() => {
                      setStep(0);
                      setOtp(["", "", "", ""]);
                      setOtpError("");
                    }}
                  >
                    Use a different email
                  </button>
                </form>
              )}
              {error && <div className="error_text">{error}</div>}
              <div className="sign_splitter">
                <span>or</span>
              </div>
              <div className="auth_google_wrapper">
                <div id="googleSignInDiv"></div>
                {!googleScriptReady && (
                  <button
                    type="button"
                    className="gray_btn auth_secondary"
                    disabled
                  >
                    {googleLoading ? null : "Loading Google Sign-In…"}
                  </button>
                )}
                {googleLoading && (
                  <DotLoader color="#7B5CFF" loading={true} size={24} />
                )}
              </div>
              {ATOZAS_SSO_ENABLED && (
                <button
                  type="button"
                  className="gray_btn auth_secondary atozas_sso_btn"
                  onClick={startAtozasLogin}
                >
                  <ShieldIcon />
                  <span>Continue with ATOZAS login</span>
                </button>
              )}
              <p className="auth_new_here">New here?</p>
              <button
                type="button"
                className="open_signup"
                onClick={() => setVisible(true)}
              >
                <UserPlusIcon />
                <span>Create New Account</span>
              </button>
              <p className="auth_privacy">
                <ShieldIcon />
                <span>Your privacy and data are safe with us.</span>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
